import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import TagsList from '@dustin-web/microsite-components/src/tag/list'
import { SearchRankingsInfo } from '@dustin-web/product-components/src/search-rankings-info'
import type { SearchPageQuery } from '@productsite/src/graphql/generated/graphql-types'
import { useMemo } from 'react'

type DeepRequired<T> = {
  [K in keyof T]-?: DeepRequired<NonNullable<T[K]>>
}

type Props = {
  hits: number
  phrase: string
  autocorrectedPhrase?: string | null
  relatedSearches?: DeepRequired<SearchPageQuery['searchPage']['relatedSearches']>
}

const htmlEntities: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, character => htmlEntities[character])

export const SearchHeader = ({ hits, phrase, autocorrectedPhrase, relatedSearches }: Props) => {
  const { translate } = useTranslations()

  const searchPrase = useMemo(() => {
    try {
      return escapeHtml(decodeURIComponent(phrase))
    } catch {
      return escapeHtml(phrase)
    }
  }, [phrase])

  if (!hits) {
    return null
  }

  return (
    <div className="col-12 u-pt-24 u-pb-24">
      <h1 className="h2">
        <span
          className="u-overflow-wrap"
          dangerouslySetInnerHTML={{
            __html: translate('Search_Page_Title', {
              searchPhraseString: `<b>“${searchPrase}”</b>`,
            }),
          }}
        />
        <SearchRankingsInfo headingLevel={2} />
      </h1>
      <h2 className="u-mb-16 u-fs-20">
        {translate('Search_Page_Products', { productsCount: hits })}
      </h2>
      {autocorrectedPhrase && (
        <span
          className="u-mb-20"
          dangerouslySetInnerHTML={{
            __html: translate('Search_Page_Corrected', {
              originalPhrase: `<b>${searchPrase}</b>`,
              correctedPhrase: `<b>${escapeHtml(autocorrectedPhrase)}</b>`,
            }),
          }}></span>
      )}
      {!!relatedSearches?.length && (
        <TagsList
          variant="search"
          className="u-pt-24"
          title={translate('Search_Page_Related_Searches')}
          list={relatedSearches}
        />
      )}
    </div>
  )
}
